// [DELETION-PIN] Section Paramètres — Configurer le code PIN de suppression
import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, ShieldOff, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

export function SettingsDeletionPin({ getAuthHeaders, accent }) {
  const [pinSet, setPinSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null); // 'set' | 'change' | 'remove' | null
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/settings/deletion-pin/status`, getAuthHeaders());
      setPinSet(!!res.data?.pin_set);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setMode(null);
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
  };

  const validPin = (p) => /^\d{4,6}$/.test(p);

  const submit = async () => {
    if (mode === "remove") {
      if (!validPin(currentPin)) return toast.error("Saisissez le code PIN actuel (4 à 6 chiffres)");
      setSaving(true);
      try {
        await axios.delete(`${API}/settings/deletion-pin`, {
          ...getAuthHeaders(),
          data: { pin: currentPin },
        });
        toast.success("Code PIN supprimé");
        setPinSet(false);
        reset();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur");
      } finally { setSaving(false); }
      return;
    }

    if (!validPin(newPin)) return toast.error("Le PIN doit contenir 4 à 6 chiffres");
    if (newPin !== confirmPin) return toast.error("Les codes PIN ne correspondent pas");
    if (mode === "change" && !validPin(currentPin)) return toast.error("Saisissez le code PIN actuel");

    setSaving(true);
    try {
      const body = { pin: newPin };
      if (mode === "change") body.current_pin = currentPin;
      await axios.post(`${API}/settings/deletion-pin`, body, getAuthHeaders());
      toast.success(mode === "set" ? "Code PIN configuré" : "Code PIN mis à jour");
      setPinSet(true);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="s-card" data-testid="deletion-pin-section">
      <div className="s-card-header">
        <div className="s-card-title-row">
          {pinSet ? (
            <ShieldCheck size={18} style={{ color: accent }} />
          ) : (
            <ShieldOff size={18} className="text-muted-foreground" />
          )}
          <h2 className="s-card-title">Code PIN de suppression</h2>
        </div>
      </div>
      <p className="s-card-subtitle">
        Exigez ce code pour toute suppression d'article ou de catégorie par un membre de l'équipe.
      </p>

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : mode ? (
        <div className="mt-4 space-y-3" data-testid="pin-form">
          {(mode === "change" || mode === "remove") && (
            <div>
              <label className="block text-[12px] text-muted-foreground mb-1.5 font-medium">
                Code PIN actuel
              </label>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={currentPin}
                onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, ""))}
                className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-foreground text-[16px] tracking-widest focus:outline-none focus:border-foreground/40"
                placeholder="••••"
                data-testid="pin-current"
              />
            </div>
          )}
          {mode !== "remove" && (
            <>
              <div>
                <label className="block text-[12px] text-muted-foreground mb-1.5 font-medium">
                  Nouveau code PIN (4 à 6 chiffres)
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-foreground text-[16px] tracking-widest focus:outline-none focus:border-foreground/40"
                  placeholder="••••"
                  data-testid="pin-new"
                />
              </div>
              <div>
                <label className="block text-[12px] text-muted-foreground mb-1.5 font-medium">
                  Confirmer le code
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={6}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                  className="w-full bg-secondary border border-border rounded-xl px-4 py-3 text-foreground text-[16px] tracking-widest focus:outline-none focus:border-foreground/40"
                  placeholder="••••"
                  data-testid="pin-confirm"
                />
              </div>
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button
              onClick={reset}
              className="flex-1 h-11 rounded-xl bg-secondary text-foreground font-medium text-[13px] hover:bg-muted transition-colors"
              data-testid="pin-cancel"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="flex-1 h-11 rounded-xl text-white font-semibold text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-40"
              style={{ backgroundColor: mode === "remove" ? "#EF4444" : accent }}
              data-testid="pin-submit"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "remove" ? (
                <><Trash2 className="w-4 h-4" /> Supprimer</>
              ) : (
                <><Save className="w-4 h-4" /> Enregistrer</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-secondary/60 border border-border mb-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: pinSet ? `${accent}18` : "hsl(var(--muted))", color: pinSet ? accent : "hsl(var(--muted-foreground))" }}
            >
              {pinSet ? <ShieldCheck size={18} /> : <ShieldOff size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-foreground">
                {pinSet ? "Protection activée" : "Aucun code PIN configuré"}
              </p>
              <p className="text-[11.5px] text-muted-foreground">
                {pinSet
                  ? "Les vendeurs devront saisir votre code pour supprimer."
                  : "Les vendeurs peuvent supprimer sans validation."}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!pinSet ? (
              <button
                onClick={() => setMode("set")}
                className="flex-1 min-w-[140px] h-11 rounded-xl text-white font-semibold text-[13px]"
                style={{ backgroundColor: accent }}
                data-testid="pin-btn-set"
              >
                Configurer un code PIN
              </button>
            ) : (
              <>
                <button
                  onClick={() => setMode("change")}
                  className="flex-1 min-w-[120px] h-11 rounded-xl text-white font-semibold text-[13px]"
                  style={{ backgroundColor: accent }}
                  data-testid="pin-btn-change"
                >
                  Modifier le PIN
                </button>
                <button
                  onClick={() => setMode("remove")}
                  className="flex-1 min-w-[120px] h-11 rounded-xl border border-red-500/40 text-red-500 hover:bg-red-500/10 font-medium text-[13px] transition-colors"
                  data-testid="pin-btn-remove"
                >
                  Retirer le PIN
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
